import { useEffect, useMemo, useState } from "react";
import { emptyResource, followResource, loadResource, type Resource } from "./resource";

export interface RecordResource<T> extends Resource<T> {
  receivedAt: number | null;
  refresh: () => void;
}

export function useResource<K, T>(
  key: K | undefined,
  read: (key: K, signal: AbortSignal) => Promise<T>,
  again?: (data: T) => boolean,
): RecordResource<T> {
  const [attempt, setAttempt] = useState(0);
  // A route visit, explicit refresh, or reader change owns a new observation.
  const request = useMemo(() => ({ key, read, again, attempt }), [key, read, again, attempt]);
  const [observed, setObserved] = useState<{ request: typeof request; state: Resource<T>; receivedAt: number | null } | null>(null);
  useEffect(() => {
    if (request.key === undefined) {
      setObserved({ request, state: { data: null, error: "Invalid record identifier" }, receivedAt: null });
      return;
    }
    const key = request.key;
    const read = (signal: AbortSignal) => request.read(key, signal);
    const publish = (state: Resource<T>, received = state.data !== null) => setObserved(previous => ({
      request, state,
      receivedAt: received ? Date.now() : state.data !== null && previous?.request === request ? previous.receivedAt : null,
    }));
    return request.again ? followResource(read, publish, request.again) : loadResource(read, publish);
  }, [request]);
  // Effects run after render. Never expose a previous route or attempt first.
  const current = observed?.request === request ? observed : null;
  return {
    ...(current?.state ?? emptyResource<T>()),
    receivedAt: current?.receivedAt ?? null,
    refresh: () => setAttempt(value => value + 1),
  };
}
